import { useState, useEffect, useCallback, memo } from 'react'
import { Button, Switch } from '@you/ui'
import { Icon } from '@/components/Icon'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DepartmentSelector from '@/components/Agents/DepartmentSelector'
import { usePublishAgent,useGetDraftByAgentId } from '@/api/hooks/useAgent'
import type { AgentRes } from '@/api/types/agent.type'
import { useUserStore } from '@/stores/userStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { toast } from '@you/ui'

// 模块容器组件 - 使用memo优化性能
interface ModuleContainerProps {
  children: React.ReactNode
  title: string
  description: string
  extra?: React.ReactNode
}

const ModuleContainer = memo<ModuleContainerProps>(({
  children,
  title,
  description,
  extra,
}) => {
  return (
    <div className="space-y-2">
      <h2 className="text-lg font-medium text-gray-900">{title}</h2>
      <div className="p-4 rounded-lg bg-[#FCFCFF] border border-[#E9DFFF]">
        <div className="mb-4 flex justify-between items-start">
          <p className="text-lg leading-relaxed text-[#999999]">
            {description}
          </p>
          {extra}
        </div>
        {children}
      </div>
    </div>
  )
})

// 创建者内容组件 - 使用memo优化性能
interface CreatorContentProps {
  isLoading: boolean
  agentDetail: AgentRes | null
  isCurrentUser: boolean
}

const CreatorContent = memo<CreatorContentProps>(({
  isLoading,
  agentDetail,
  isCurrentUser,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 bg-[#F5F4FE] rounded-lg py-2 px-4">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '60%' }}></div>
        </div>
      </div>
    )
  }

  if (!agentDetail) {
    return (
      <div className="text-center text-gray-500 py-4">
        未找到智能体信息
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-2 bg-[#F5F4FE] rounded-lg py-2 px-4">
      <div className="flex-1 text-[#2A0F67] font-bold">
        <span>{agentDetail.creator || '未知用户'}({agentDetail.phone || '未知电话号码'})</span>
        {/* 如果是当前登陆者，则显示当前登陆者 */}
        {isCurrentUser && (
          <span className="text-[#8B5CF6] bg-[#8752FF29] px-2 py-1 rounded-full text-sm ml-2">
            自己
          </span>
        )}
      </div>
      {/* 当前登陆者显示转让按钮 */}
      {isCurrentUser && (
        <Button variant="link">
          转让
        </Button>
      )}
    </div>
  )
})

// 使用权限内容组件 - 使用memo优化性能
interface PermissionContentProps {
  isLinkage: boolean
  agentDetail: AgentRes | null
  onSelectionChange: (selectedIds: string[]) => void
}

const PermissionContent = memo<PermissionContentProps>(({
  isLinkage,
  agentDetail,
  onSelectionChange,
}) => {
  return (
    <>
      <DepartmentSelector 
        isLinkage={isLinkage} 
        agentTableId={agentDetail?.id?.toString()}
        onSelectionChange={onSelectionChange}
      />
    </>
  )
})

const AgentPublish = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId')
  const [isLinkage, setIsLinkage] = useState(false) // 是否联动勾选
  const [agentDetail, setAgentDetail] = useState<AgentRes | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [departmentIds, setDepartmentIds] = useState<string[]>([])
  const [isPublishing, setIsPublishing] = useState(false)

  const { currentUser } = useUserStore.getState()
  const { currentWorkspace } = useWorkspaceStore.getState()

  // 发布智能体hook
  const { trigger: publishAgent } = usePublishAgent()

  const handleBack = () => {
    navigate('/agents')
  }

  // 查询智能体详情
  const { data: agentData } = useGetDraftByAgentId({ agentId: agentId || '' })

  // 模拟当前用户信息
  const isCurrentUser = agentData?.userId === currentUser?.userId

  useEffect(() => {
    if (agentData) {
      setAgentDetail(agentData)
      setIsLoading(false)
    }
  }, [agentData])

  // 发布智能体
  const handlePublish = async () => {
    if (!agentDetail || !agentId) {
      toast.error('智能体信息不完整，无法发布')
      return
    }

    setIsPublishing(true)

    try {
      const publishData = {
        agentId: agentId,
        departmentIdList: departmentIds,
        id: agentDetail.id?.toString(),
        version: agentDetail.version || '0',
        workspaceId: currentWorkspace?.id || '',
      }

      await publishAgent(publishData)

      toast.success('智能体发布成功')
      // 发布成功后跳转到智能体列表页面
      navigate('/agents')
    } catch (error) {
      console.error('发布智能体失败:', error)
      toast.error('发布失败，请检查网络连接后重试')
    } finally {
      setIsPublishing(false)
    }
  }

  // 部门选择变化处理 - 使用useCallback优化性能
  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    console.log('选中的部门ID:', selectedIds)
    setDepartmentIds(selectedIds)
  }, [])

  return (
    <div className="min-h-screen">
      {/* 上部分 - 头部 */}
      <div className="w-full flex items-center justify-between">
        {/* 左侧标题 */}
        <h1 className="text-2xl font-medium" style={{ color: '#000000' }}>
          发布
        </h1>

        {/* 右侧操作按钮 */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleBack}
            className="rounded-full w-[78px]"
            disabled={isPublishing}
          >
            返回
          </Button>
          <Button
            onClick={handlePublish}
            className="gradient-gentle rounded-full w-[138px] gap-1"
            disabled={isPublishing || isLoading || !agentDetail}
          >
            <Icon name="publish" size={16} />
            {isPublishing ? '发布中...' : '发布智能体'}
          </Button>
        </div>
      </div>

      {/* 下部分 - 内容区域 */}
      <div className="max-w-[1342px] mx-auto py-6">
        <div className="space-y-8">
          {/* 创建者 */}
          <ModuleContainer title="创建者" description="可以编辑和删除智能体">
            <CreatorContent
              isLoading={isLoading}
              agentDetail={agentDetail}
              isCurrentUser={isCurrentUser}
            />
          </ModuleContainer>

          {/* 使用权限 */}
          <ModuleContainer
            title="使用权限"
            description="在以下用户发布你的智能体，即表示你已充分理解并同意遵循各发布渠道服务条款(包括但不限于任何隐私政策、社区指南、数据处理协议等)"
            extra={
              <div className="flex items-center space-x-2 flex-none ml-4">
                <label className="text-lg text-[#333333]">联动勾选</label>
                <Switch checked={isLinkage} onCheckedChange={setIsLinkage} />
              </div>
            }
          >
            <PermissionContent
              isLinkage={isLinkage}
              agentDetail={agentDetail}
              onSelectionChange={handleSelectionChange}
            />
          </ModuleContainer>
        </div>
      </div>
    </div>
  )
}

export default AgentPublish
